import { createContext, ReactNode, useContext, useEffect, useRef } from 'react'

import debounce from 'lodash/debounce'
import { createStore, StoreApi, useStore } from 'zustand'

import { Article, Category } from 'interfaces/types'
import { cycleCircularArray } from 'utils/cycleCircularArray'

import {
  ArticleStoreType,
  ArticleStoreValueType,
  ChineseTranslateEnum,
  FontSize,
} from './types'

export const FONT_SIZE: Array<FontSize> = ['small', 'medium', 'large']

const MyContext = createContext<StoreApi<ArticleStoreType>>(null as any)

const createArticleStore = (init?: ArticleStoreValueType) =>
  createStore<ArticleStoreType>((set, get) => ({
    articles: [],
    bookmarkIds: [],
    chineseTranslate: ChineseTranslateEnum.traditional,
    currentArticle: null,
    currentProgress: 0,
    fontSize: 'small',
    isBookmarked: false,
    isCommentPopupOpen: false,
    isLiked: false,
    isDisliked: false,
    totalComment: 0,
    categories: [],
    ...(init || ({} as any)),
    addToArticles: (data: Article) => {
      if (!data || get().articles.find((d) => data.id === d.id)) return
      set({ articles: get().articles.concat(data) })
    },
    addToBookmark: (data: string | number) =>
      set({ isBookmarked: true, bookmarkIds: [...get().bookmarkIds, data] }),
    removeBookmark: (data: string | number) => {
      const newBookmarks = get().bookmarkIds.filter((bid) => bid !== data)
      set({ isBookmarked: false, bookmarkIds: newBookmarks })
    },
    setArticles: (
      data: Array<Article> | ((a: Array<Article>) => Array<Article>),
    ) =>
      set((state) => ({
        articles: typeof data === 'function' ? data(state.articles) : data,
      })),
    setBookmarkIds: (data: Array<string | number>) => {
      set({ bookmarkIds: data })
    },
    setChineseTranslate: (data: ChineseTranslateEnum) =>
      set({ chineseTranslate: data }),
    setCurrentArticle: debounce(
      (data: Article | null) => set({ currentArticle: data }),
      300,
    ),
    setCurrentProgress: (data: number) => set({ currentProgress: data }),
    cycleFontSize: () =>
      set({ fontSize: cycleCircularArray(FONT_SIZE, get().fontSize) }),
    setIsBookmarked: (data: boolean) => set({ isBookmarked: data }),
    setIsCommentPopupOpen: (data: boolean) => set({ isCommentPopupOpen: data }),
    setIsLiked: (data: boolean) => set({ isLiked: data }),
    setIsDisliked: (data: boolean) => set({ isDisliked: data }),
    setTotalComment: (data: number | null) => set({ totalComment: data }),
    setCategories: (data: Array<Category>) => set({ categories: data }),
    setShowDictionaryShortcutBtn: (data: boolean) =>
      set({ showDictionaryShortcutBtn: data }),
    setInFirstViewPort: (data: boolean) => set({ inFirstViewport: data }),
  }))

export const ArticleStoreProvider = ({
  init,
  refreshKey,
  children,
}: {
  init?: Partial<ArticleStoreType>
  refreshKey?: string
  children?: ReactNode
}) => {
  const storeRef = useRef(createArticleStore(init as ArticleStoreType))
  const store = storeRef.current // or any better solution

  //  handle client side routing, reset new data from props
  useEffect(() => {
    refreshKey && storeRef.current.setState(init as ArticleStoreType)
  }, [refreshKey])

  return <MyContext.Provider value={store}>{children}</MyContext.Provider>
}

// wrapper for zustand useStore function
export const useArticleStore = (
  selector: (state: ArticleStoreType) => any,
  equalityFn?: (a: unknown, b: unknown) => boolean,
) => {
  const store = useContext(MyContext)
  const slice = useStore(store, selector, equalityFn)

  return slice as ArticleStoreType
}
